import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Spinner } from '@hopper/loading';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Use invariant() to assert state which your program assumes to be true.
 *
 * Provide sprintf-style format (only %s is supported) and arguments
 * to provide information about what broke and what you were
 * expecting.
 *
 * The invariant message will be stripped in production, but the invariant
 * will remain to ensure logic does not differ in production.
 */

var invariant = function(condition, format, a, b, c, d, e, f) {
  if (process.env.NODE_ENV !== 'production') {
    if (format === undefined) {
      throw new Error('invariant requires an error message argument');
    }
  }

  if (!condition) {
    var error;
    if (format === undefined) {
      error = new Error(
        'Minified exception occurred; use the non-minified dev environment ' +
        'for the full error message and additional helpful warnings.'
      );
    } else {
      var args = [a, b, c, d, e, f];
      var argIndex = 0;
      error = new Error(
        format.replace(/%s/g, function() { return args[argIndex++]; })
      );
      error.name = 'Invariant Violation';
    }

    error.framesToPop = 1; // we don't care about invariant's own frame
    throw error;
  }
};

var browser = invariant;

var ButtonContent = function ButtonContent(_a) {
  var loaderColor = _a.loaderColor,
    isLoading = _a.isLoading,
    children = _a.children;
  return /*#__PURE__*/React.createElement(React.Fragment, null, !isLoading && children, isLoading && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "invisible"
  }, children), /*#__PURE__*/React.createElement(Spinner, {
    size: 40,
    isCentered: true,
    color: loaderColor
  })));
};
ButtonContent.propTypes = {
  loaderColor: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired
};

var SIZE_STYLES = {
  md: "h-4xl px-lg",
  sm: "h-3xl px-md"
};
var VARIANTS_TEXT_COLORS_MAPPING = {
  primary: "text-link tw:text-tw-primary-500",
  secondary: "text-light",
  success: "text-success tw:text-tw-success-500",
  danger: "text-danger tw:text-tw-danger-500",
  info: "text-info"
};
var VARIANTS_BG_COLORS_MAPPING = {
  primary: "bg-link tw:bg-tw-primary-500 tw:hover:bg-tw-primary-900",
  secondary: "bg-light tw:bg-white tw:hover:bg-tw-neutral-100",
  success: "bg-success tw:bg-tw-success-500 tw:hover:bg-tw-success-900",
  danger: "bg-danger tw:bg-tw-danger-500 tw:hover:bg-tw-danger-900",
  info: "bg-info"
};
var VARIANTS_BORDERS_MAPPING = {
  primary: "",
  secondary: "tw:border tw:border-1 tw:border-tw-neutral-500",
  success: "",
  danger: "",
  info: ""
};
var DEFAULT_VARIANT = "primary";
var DEFAULT_SIZE = "md";
var DEFAULT_LOADER_COLOR = "body";
var DEFAULT_APPEARANCE = "default";
function getCorrectSize(size) {
  return size || DEFAULT_SIZE;
}
var Button = function Button(_a) {
  var _b = _a.variant,
    variant = _b === void 0 ? DEFAULT_VARIANT : _b,
    _c = _a.appearance,
    appearance = _c === void 0 ? DEFAULT_APPEARANCE : _c,
    _d = _a.size,
    size = _d === void 0 ? DEFAULT_SIZE : _d,
    _e = _a.className,
    className = _e === void 0 ? "" : _e,
    _f = _a.isLoading,
    isLoading = _f === void 0 ? false : _f,
    _g = _a.disabled,
    disabled = _g === void 0 ? false : _g,
    href = _a.href,
    children = _a.children,
    onClick = _a.onClick,
    type = _a.type,
    otherProps = __rest(_a, ["variant", "appearance", "size", "className", "isLoading", "disabled", "href", "children", "onClick", "type"]);
  var sizeClass = SIZE_STYLES[getCorrectSize(size)];
  var isDefaultAppearance = appearance === DEFAULT_APPEARANCE;
  var textColor = getTextColor(appearance, variant, disabled);
  var loaderColor = isDefaultAppearance ? getLoaderColor(appearance, variant, disabled) : DEFAULT_LOADER_COLOR;
  var bgColorClass = disabled ? "bg-light" : getBackgroundColor(appearance, variant);
  var defaultAppearanceClass = isDefaultAppearance && "hover:shadow tw:hover:shadow-none ".concat(bgColorClass);
  var isButtonElement = !!(onClick || type);
  var cssClasses = classNames(["font-bold", "rounded-base", "inline-flex", "items-center", "relative", textColor, {
    "focus:outline-none": isButtonElement
  }, {
    "cursor-pointer": !disabled && !isLoading
  }, {
    "cursor-default pointer-events-none": disabled || isLoading
  }, getBorderClasses(appearance, variant, disabled), defaultAppearanceClass, sizeClass, className]);
  href && browser(!onClick, "Links which look like button should be handled only via href, not onClick");
  if (href) {
    return /*#__PURE__*/React.createElement("a", __assign({
      className: cssClasses,
      href: disabled ? "" : href
    }, otherProps), /*#__PURE__*/React.createElement(ButtonContent, {
      loaderColor: loaderColor,
      isLoading: isLoading
    }, children));
  } else if (isButtonElement) {
    return /*#__PURE__*/React.createElement("button", __assign({
      onClick: disabled ? undefined : onClick,
      className: cssClasses,
      disabled: disabled || isLoading,
      type: type
    }, otherProps), /*#__PURE__*/React.createElement(ButtonContent, {
      loaderColor: loaderColor,
      isLoading: isLoading
    }, children));
  } else {
    return /*#__PURE__*/React.createElement("span", __assign({
      className: cssClasses
    }, otherProps), /*#__PURE__*/React.createElement(ButtonContent, {
      loaderColor: loaderColor,
      isLoading: isLoading
    }, children));
  }
};
function getBorderClasses(appearance, variant, disabled) {
  if (appearance === "ghost") {
    return variant === "secondary" && !disabled ? "border border-dark" : "border";
  }
  return VARIANTS_BORDERS_MAPPING[variant];
}
function getLoaderColor(appearance, variant, disabled) {
  if (disabled) {
    return "muted";
  } else if (["minimal", "ghost"].includes(appearance)) {
    return "darkest";
  } else if ("secondary" === variant) {
    return "body";
  } else {
    return "lightest";
  }
}
function getTextColor(appearance, variant, disabled) {
  if (disabled) {
    return "text-muted";
  } else if ("secondary" === variant) {
    return "text-body";
  } else if (["minimal", "ghost"].includes(appearance)) {
    return getColorForTextVariant(variant);
  } else {
    return "text-lightest";
  }
}
function getBackgroundColor(appearance, variant) {
  if (appearance === "ghost") {
    return "bg-transparent";
  } else {
    return getColorForBgVariant(variant);
  }
}
function getColorForTextVariant(variant) {
  return VARIANTS_TEXT_COLORS_MAPPING[variant];
}
function getColorForBgVariant(variant) {
  return VARIANTS_BG_COLORS_MAPPING[variant];
}
Button.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(SIZE_STYLES)),
  variant: PropTypes.oneOf(Object.keys(VARIANTS_TEXT_COLORS_MAPPING)),
  appearance: PropTypes.oneOf(["default", "minimal", "ghost"]),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  href: PropTypes.string,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  isLoading: PropTypes.bool
};

export { Button };
//# sourceMappingURL=index.es.js.map
